<template>
  <v-container id="mapCtrls" fluid>
    <v-card class="mx-auto" style="padding: 5px" flat>
        <v-row justify="center" no-gutters>
          <v-col sm='12' lg='6'>
            <v-expansion-panels hover flat>
              <v-expansion-panel>
                <v-expansion-panel-header class='expansion-panel-header-dense'>
                  <h2>Generate reports from any site</h2>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                <v-card-subtitle>
                  <v-row align="center" justify="space-between">
                    <span class='description'>
                      Please select the report type and use the data picker to generate a report for a specific time and place.
                    </span>
                  </v-row>
                </v-card-subtitle>
                <v-container>
                      <v-row>
                        <v-col>
                          <div class="select-spacing">
                            <h3 class='description'>
                                Select the dataset
                              </h3>
                            <v-select :value="currentDataset" :items="datasetList" item-text="label" item-value='title' label="Select Dataset"
                              outlined :error-messages="datasetInput" hide-details
                              @input="setState('currentDataset', $event)">
                              <option value></option>
                            </v-select>
                          </div>
                          <div v-show="currentDataset !== null" class="select-spacing">
                            <h3 class='description'>
                                Select the site location
                              </h3>
                            <v-autocomplete v-show="!chosenTown" :value="currentLocation" :items="locationList" outlined
                              :error-messages="locationInput" label="Select Location"
                              hide-details persistent-hint
                              @input="setState('currentLocation', $event)"></v-autocomplete>
                              <v-alert
                                v-show="chosenTown"
                                outlined
                                type="info"
                              >
                              You are currently creating a report for the above grouped sites. Select 'Reset' in the legend if this is not intended
                            </v-alert>
                          </div>
                          <div v-show="currentDataset !== null" class="select-spacing">
                            <h3 class='description'>
                              Select the report type
                            </h3>
                            <v-select :value="currentReport" :items="reportList" label="Select Report"
                              hide-details outlined :error-messages="reportInput"
                              @input="setState('currentReport', $event)"></v-select>
                          </div>
                          <v-expand-transition>
                            <v-alert v-show="comparisonSwitch" type="info">Your time periods must be the same duration for
                              comparison reports.</v-alert>
                          </v-expand-transition>
                          <div class='comparison-switch-container'>
                            <v-switch :input-value="comparisonSwitch" dense hide-details label="Is this a comparison report?"
                              @change="toggle($event)">
                            </v-switch>
                          </div>
                              <h3 class='description'>
                                Add supporting documents
                              </h3>
                          <v-card>
                            <v-card-subtitle>Links to additional documentation for selected site
                              will display here</v-card-subtitle>
                            <v-card-text>
                              <ul>
                                <li v-for="(link, index) in s3Links" :key="index">
                                  <a target="_blank" :href="link.href">{{
                                    link.label
                                  }}</a>
                                </li>
                              </ul>
                            </v-card-text>
                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <FileModal />
                            </v-card-actions>
                          </v-card>
                        </v-col>
                      </v-row>
                </v-container>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
          <v-col sm='12' lg='6'>
            <v-expansion-panels hover flat>
              <v-expansion-panel>
                <v-expansion-panel-header class='expansion-panel-header-dense'>
                  <h2>Data range for any site</h2>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                <v-card-subtitle class="date-subtitle-spacing">
                  <v-row align="center" justify="space-between">
                    <span class='datepicker-header'>
                      Please use the date picker to select a range or dates for the selected site (some dates maybe greyed out if no data is available)
                    </span>
                    <h3 class='datepicker-header'>Data available for this site:
                        <span v-show="dataTimeRange.start && dataTimeRange.end">
                          {{ dataTimeRange.start }} - {{ dataTimeRange.end }}
                        </span>
                        <span v-show="!currentDataset" >Please select dataset </span>
                        <span v-show="currentLocation.length <= 0 && currentDataset"> Please select location </span>
                        <span v-show="currentReport.length <= 0 && currentDataset && currentLocation.length > 0"> Please select report </span>
                        <span v-show="!dataTimeRange.start && !dataTimeRange.end && currentReport.length > 0 && currentDataset && currentLocation.length > 0">No data returned for selected combination, please check the dataset</span>
                      </h3>
                  </v-row>
                </v-card-subtitle>
                <v-container>
                  <v-row>
                    <v-col class="d-lg-block">
                        <v-row justify="space-between" align="center">
                          <v-col cols="6">
                            <v-slide-x-transition hide-on-leave>
                              <span v-show="comparisonSwitch" class='datepicker-header'>
                                Current data range selected:
                                <span>
                                  {{ dates[0] }} - {{ dates[1] }}
                                </span>
                              </span>
                            </v-slide-x-transition>
                            <v-slide-x-transition hide-on-leave>
                              <span v-show="!comparisonSwitch" class='datepicker-header'>
                                Current data range selected:
                                <span>
                                  {{ dates[0] }} - {{ dates[1] }}
                                </span>
                              </span>
                            </v-slide-x-transition>
                          </v-col>
                          <v-slide-x-transition hide-on-leave>
                            <v-col v-show="comparisonSwitch" cols="6">
                              <span class='datepicker-header'>
                                Comparison report date range selected:
                                <span>
                                  {{ dates[0] }} - {{ dates[1] }}
                                </span>
                              </span>
                            </v-col>
                          </v-slide-x-transition>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <!-- year -->
                            <div style="padding: 0 12px;">
                              <v-date-picker ref="startDateBox" v-model="chosenMonth" type="month" color="#143b6a" height='40'
                                :allowed-dates="allowedMonths" :min="dataTimeRange.start" :max="dataTimeRange.end" reactive
                                active-picker="YEAR" :full-width="true" flat></v-date-picker>
                            </div>
                          </v-col>
                          <v-col cols="12">
                            <div style="display: flex">
                              <v-fade-transition hide-on-leave mode="in-out">
                                <v-col v-show="!comparisonSwitch" cols="12">
                                  <div >
                                    <v-date-picker ref="startDateBox" v-model="dates" range color="#016cff"
                                      :allowed-dates="allowedDates" reactive :min="dataTimeRange.start" :max="dataTimeRange.end"
                                      active-picker="YEAR" :full-width="true" :events="getEvents" flat></v-date-picker>
                                  </div>
                                </v-col>
                              </v-fade-transition>
                              <v-fade-transition hide-on-leave mode="out-in">
                                <v-col v-show="comparisonSwitch">
                                  <div >
                                    <v-date-picker ref="startDateBox" v-model="dates" range color="#016cff"
                                      :allowed-dates="allowedDates" reactive :min="dataTimeRange.start" :max="dataTimeRange.end"
                                      active-picker="YEAR" :full-width="true" :events="getEvents" flat></v-date-picker>
                                  </div>
                                </v-col>
                              </v-fade-transition>
                              <v-fade-transition hide-on-leave>
                                <v-col v-show="comparisonSwitch" cols="6">
                                  <v-date-picker ref="comparisonDateBox" v-model="comparisonDates" range color="#016cff"
                                    :allowed-dates="allowedDates" reactive :min="dataTimeRange.start" :max="dataTimeRange.end"
                                    :active-picker="'YEAR'" :full-width="true" :events="getEvents" flat></v-date-picker>
                                </v-col>
                              </v-fade-transition>
                            </div>
                          </v-col>
                        </v-row>
                    </v-col>
                  </v-row>
                </v-container>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
        <v-divider class="spacer"></v-divider>
          <v-card-actions>
            <v-row>
              <v-col class="text-right" align-self="center" xs="12" md="12" lg="12" xl="12">
                <v-btn :loading="getLoadStatus" class='btn-main mr-5'
                  color='#006cff'
                  outlined
                  rounded
                  :disabled="getLoadStatus ||
                    dates.length !== 2 ||
                    !currentDataset ||
                    (!currentLocation && !chosenTown) ||
                    !currentReport ||
                    (comparisonSwitch && comparisonDates.length !== 2)"
                  @click="downloadPreview({ isComparison: comparisonSwitch, dates: dates, comparisonDates: comparisonDates })">
                Preview report
              </v-btn>
                <v-btn
                  :loading="getLoadStatus"
                  class='btn-main'
                  color='#006cff'
                  outlined
                  rounded
                  :disabled="getLoadStatus ||
                    dates.length !== 2 ||
                    !currentDataset ||
                    (!currentLocation && !chosenTown) ||
                    !currentReport ||
                    (comparisonSwitch && comparisonDates.length !== 2)"
                  @click="generateReport({ isComparison: comparisonSwitch, dates: dates, comparisonDates: comparisonDates })">
                  Generate report
                </v-btn>
              </v-col>
              <v-col v-show="generatedReportRes" cols="12">
                <v-alert style="margin: 0" :type="generatedReportStatus === 200 ? 'success' : 'error'">{{
                  generatedReportRes
                }}</v-alert>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
  </v-container>
</template>

<script>
/* eslint-disable import/no-unresolved */
import { mapGetters, mapActions, mapState } from 'vuex';
import FileModal from '@/components/AddFileModal.vue';

export default {
  name: 'ReportingControls',
  components: {
    FileModal,
  },
  data: () => {
    return {
      date: null,
      dates: [],
      comparisonDates: [],
      chosenMonth: null,
    };
  },
  computed: {
    ...mapState({
      datasetInput: (state) => state.datasets.datasetInput,
      reportInput: (state) => state.datasets.reportInput,
      locationInput: (state) => state.datasets.locationInput,
      comparisonSwitch: (state) => state.datasets.comparisonSwitch,
      flaggedData: (state) => state.datasets.flaggedData,
      chosenTown: (state) => state.datasets.chosenTown,
    }),
    ...mapGetters([
      'datasetList',
      'currentDataset',
      'locationList',
      'currentLocation',
      'currentReport',
      'reportList',
      'generatedReportRes',
      'generatedReportStatus',
      'getLoadStatus',
      'restrictedDates',
      'restrictedMonths',
      'dataTimeRange',
      's3Links',
      'getGroupFilteredSites'
    ]),
  },
  watch: {
    comparisonSwitch(n) {
      if (!n) {
        this.comparisonDates = [];
      }
    },
    currentDataset() {
      this.fetchAvailableReports();
      if (this.getGroupFilteredSites.length > 0) {
        this.dates = [];
        this.fetchRestrictedDates();
      }
      this.$store.commit('setVisibleNodes');
    },
    currentLocation(site) {
      if (site) {
        this.dates = [];
        this.fetchRestrictedDates();
        // this.getFlaggedData(site);
      }
    },
    currentReport(report) {
      if (report) {
        this.dates = [];
        this.fetchRestrictedDates();
      }
    },
    startDateMenu(val) {
      if (val) {
        setTimeout(() => this.defaultPicker('startDatePickerMenu'));
      }
    },
    endDateMenu(val) {
      if (val) {
        setTimeout(() => this.defaultPicker('endDatePickerMenu'));
      }
    },
    dataTimeRange(val) {
      if (val) {
        this.dates = [val.start, val.end];
        if (this.comparisonSwitch) {
          this.comparisonDates = [val.start, val.end];
        }
      }
    },
    chosenMonth(val) {
      if (val) {
        let trackIndex = null;
        const contains = this.restrictedDates.some((element, index) => {
          trackIndex = index;
          return element.indexOf(val) >= 0;
        });
        if (contains) {
          this.dates = [this.restrictedDates[trackIndex]];
        }
      }
    },
    getGroupFilteredSites(val) {
      if (val.length > 0 && !!this.currentDataset) {
        this.dates = [];
        this.fetchRestrictedDates();
      }
    }
  },
  mounted() {
    // this.$refs.startDateBox.activePicker = 'MONTH';
    // this.$refs.endDateBox.activePicker = 'MONTH';
  },
  methods: {
    ...mapActions([
      'fetchData',
      'generateReport',
      'generatePreview',
      'fetchAvailableReports',
      'fetchRestrictedDates',
      'getFlaggedData',
      'fetchS3Links',
    ]),
    async downloadPreview(params) {
      const response = await this.generatePreview(params);
      if (response.format && response.format === 'new') {
        if (response.data.html) {
          window.open(response.data.html);
          URL.revokeObjectURL(response.data.html);
        }
      } else {
        const blob = new Blob([response.data], { type: 'application/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${this.currentDataset.title}_${this.currentLocation}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    },
    toggle(event) {
      this.$store.commit('setComparisonSwitch', { isComparison: event });
    },
    defaultPicker(picker) {
      this.$refs[picker].activePicker = 'MONTH';
    },
    allowedDates(d) {
      if (this.restrictedDates) {
        return this.restrictedDates.indexOf(d) > -1;
      }
      return false;
    },
    allowedMonths(m) {
      // year-month-day
      if (this.restrictedMonths) {
        return this.restrictedMonths.indexOf(m) > -1;
      }
      return false;
    },
    setState(name, value) {
      this.$store.commit('resetValidation', name);
      this.$store.commit('setState', { name, value });
      if (name === 'currentDataset') {
        this.fetchData();
      }
    },
    getEvents(date) {
      const coloursByFlag = { questionable: 'orange', replaced: 'blue' };
      if (this.flaggedData && this.flaggedData.length > 0) {
        const exists = this.flaggedData.some((flagged) => {
          return flagged.flag_timestamp.substr(0, 10) === date;
        });
        if (exists !== -1) {
          const { flagType } = this.flaggedData[exists];
          return [coloursByFlag[flagType]];
        }
      }
      return null;
    },
  },
};
</script>

<style scoped lang="scss">
.spacer {
  margin: 5px 0;
}

.comparison-switch-container {
  margin-bottom: 15px;
}

label {
  line-height: 20px;
  padding: 5px;
}

.action-ctrls {
  display: flex;
}

.action-btn {
  color: #fff;
  margin-left: 10px;
}

.select-spacing {
  padding: 0 0 15px 0;
}

.v-menu__content {
  z-index: 100 !important
}

.date-subtitle-spacing {
  margin-bottom: 15px;
}

.datepicker-header {
  color: #143b6a;
  font-size: 1rem;

  span {
    color: black;
  }
}
</style>
